import axios, { AxiosInstance, AxiosResponse } from "axios";

const BASE_API_URL = import.meta.env.VITE_BASE_API_URL ?? "";

export type ApiRequestOptions = {
  /**
   * The axios instance: the default one signs the user out on a 401 (`AxiosInterceptor`), `uninterceptedAxiosInstance`
   * doesn't (e.g. for signing in, where a 401 is a wrong password)
   */
  instance?: AxiosInstance;
  /** Sends the session cookie; true by default, the requests of a user that isn't signed in don't need it */
  withCredentials?: boolean;
  /** The statuses that aren't errors, e.g. a 404 for an image that isn't the user's */
  validateStatus?: (status: number) => boolean;
};

/**
 * A request to the Trackbook API (`<VITE_BASE_API_URL>/<endpoint>`) with its whole response, e.g. for its status.
 * Every request names the app (`agent`) and the language of the messages it returns (`lng`).
 */
export const requestApi = <Response = unknown>(
  endpoint: string,
  lng: string,
  data: object = {},
  {
    instance = axios,
    withCredentials = true,
    validateStatus,
  }: ApiRequestOptions = {}
): Promise<AxiosResponse<Response>> =>
  instance.post<Response>(
    `${BASE_API_URL}/${endpoint}`,
    { ...data, agent: "Trackbook", lng },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials,
      ...(validateStatus && { validateStatus }),
    }
  );

/** A request to the Trackbook API (see `requestApi`), resolves with the response's data */
export const postToApi = async <Response = unknown>(
  endpoint: string,
  lng: string,
  data: object = {},
  options: ApiRequestOptions = {}
) => (await requestApi<Response>(endpoint, lng, data, options)).data;

/** A GET request to the Trackbook API with `agent` and `lng` in the query (for requests that read public data) */
export const getFromApi = async <Response = unknown>(
  endpoint: string,
  lng: string,
  { instance = axios }: Pick<ApiRequestOptions, "instance"> = {}
) => {
  const query = new URLSearchParams({ agent: "Trackbook", lng });
  const { data } = await instance.get<Response>(
    `${BASE_API_URL}/${endpoint}?${query.toString()}`,
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
};
